/**
 * Bills API
 * All bill-related API operations
 */

import { getStoredToken, getStoredCompanyData } from "../../../../services/api";
import {
  BillDTO,
  BillsResponseDTO,
  BillDataDTO,
  BillsQueryParamsDTO,
  BillOperationResponseDTO,
} from "../models/BillDTOs";
import { API_BASE_URL, CORE_API_URL } from "../../../../config/api";
import type { ApprovalAuditPayload } from "../../../../shared/utils/reviewWorkflow";

function withApprovalAudit(
  body: Record<string, unknown>,
  approvalAudit?: ApprovalAuditPayload,
): Record<string, unknown> {
  if (!approvalAudit) {
    return body;
  }
  return {
    ...body,
    approval_audit: {
      reason: approvalAudit.reason,
      change_details: approvalAudit.change_details,
    },
  };
}

// API Error Class
// Exported for ZOUR-65: the review page needs `data.reason` to tell a refusal
// that means "nothing to re-run" from one that means "the re-run failed".
export class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

// HTTP Status Constants
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

/**
 * Helper: Parse bill_json from string to object
 */
export const parseBillJson = (
  billJsonString: string | BillDataDTO | null
): BillDataDTO | null => {
  if (!billJsonString) {
    return null;
  }

  try {
    let parsedData: any;

    // Handle both string and already parsed JSON
    if (typeof billJsonString === "string") {
      parsedData = JSON.parse(billJsonString);
    } else {
      parsedData = billJsonString;
    }

    // Set original_account_id for line items if not exists
    if (
      parsedData &&
      parsedData.line_items &&
      Array.isArray(parsedData.line_items)
    ) {
      parsedData.line_items = parsedData.line_items.map((item: any) => ({
        ...item,
        original_account_id: item.account_id,
      }));
    }

    return parsedData as BillDataDTO;
  } catch (error) {
    console.error("Error parsing bill_json:", error);
    return null;
  }
};

/**
 * Helper: Stringify bill_json from object to string
 */
export const stringifyBillJson = (
  billData: BillDataDTO | null
): string | null => {
  if (!billData) {
    return null;
  }

  try {
    return JSON.stringify(billData);
  } catch (error) {
    console.error("Error stringifying bill_json:", error);
    return null;
  }
};

/**
 * Fetch all bills with optional filtering and pagination
 */
export const fetchBills = async (
  params: BillsQueryParamsDTO = {}
): Promise<BillsResponseDTO> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  const queryParams = new URLSearchParams();

  // Add parameters if they exist
  if (params.limit !== undefined) {
    const limit = Math.min(params.limit, 100);
    queryParams.append("limit", limit.toString());
  }

  if (params.offset !== undefined) {
    queryParams.append("offset", params.offset.toString());
  }

  if (params.status) {
    queryParams.append("status", params.status.toString());
  }

  if (params.search) {
    queryParams.append("search", params.search);
  }

  if (params.reviewed) {
    queryParams.append("reviewed", params.reviewed.toString());
  }

  if (params.date_from) {
    queryParams.append("date_from", params.date_from);
  }

  if (params.date_to) {
    queryParams.append("date_to", params.date_to);
  }

  if (params.created_after) {
    queryParams.append("created_after", params.created_after);
  }

  if (params.created_before) {
    queryParams.append("created_before", params.created_before);
  }

  if (params.sort_by) {
    queryParams.append("sort_by", params.sort_by);
  }

  if (params.sort_order) {
    queryParams.append("sort_order", params.sort_order);
  }

  const url = `${API_BASE_URL}/api/bills${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    const data = await response.json();

    // Parse bill_json for each bill
    if (data.success && data.data && data.data.bills) {
      data.data.bills = data.data.bills.map((bill: any) => ({
        ...bill,
        bill_json: parseBillJson(bill.bill_json),
      }));
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Get a single bill by ID
 */
export const getBill = async (billId: string): Promise<BillDTO> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/bills/${billId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    const data = await response.json();

    // Parse bill_json for the single bill
    if (data.success && data.data) {
      return {
        ...data.data,
        bill_json: parseBillJson(data.data.bill_json),
      };
    }

    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

export interface RerunVendorChangeResult {
  success: boolean;
  message?: string;
  data?: { bill_id: number; audit_id: number | null; status: string };
}

export interface RecomputeStatusResult {
  status: "idle" | "recomputing" | "done" | "error" | "unknown";
  change_diff?: unknown[];
  lines_changed?: number;
  message?: string;
}

/**
 * ZOUR-65 — trigger the LOCATION-change pipeline rerun.
 *
 * Sibling of `rerunVendorChange` below and polled the same way. Moving a bill to
 * another GST registration changes whether the purchase is inter- or
 * intra-state, so the per-line tax has to be recomputed: Zoho rejects a bill
 * whose tax group disagrees with its location (code 3032).
 *
 * `locationId` null means the primary location.
 */
export const rerunLocationChange = async (
  billId: string | number,
  locationId: string | null
): Promise<RerunVendorChangeResult> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bills/rerun-location-change`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bill_id: billId, location_id: locationId }),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP error! status: ${response.status}`,
        response.status,
        data
      );
    }
    return data as RerunVendorChangeResult;
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Trigger the vendor-change pipeline rerun (GL + supply/tax + TDS).
 * Persists the chosen vendor and kicks the cron; poll getRecomputeStatus for completion.
 */
export const rerunVendorChange = async (
  billId: string | number,
  newVendorId: string,
  newVendorName: string
): Promise<RerunVendorChangeResult> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bills/rerun-vendor-change`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bill_id: billId,
          new_vendor_id: newVendorId,
          new_vendor_name: newVendorName,
        }),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP error! status: ${response.status}`,
        response.status,
        data
      );
    }
    return data as RerunVendorChangeResult;
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Poll the rerun status for a bill. The first call that observes the GL cron
 * done (stage 40) finalises the rerun server-side.
 */
export const getRecomputeStatus = async (
  billId: string | number
): Promise<RecomputeStatusResult> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/bills/recompute-status/${billId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP error! status: ${response.status}`,
        response.status,
        data
      );
    }
    return (data.data ?? { status: "unknown" }) as RecomputeStatusResult;
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Update bill JSON data
 */
export const updateBillJson = async (
  billId: string,
  billJson: BillDataDTO
): Promise<BillDTO> => {
  const token = getStoredToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/bills/update-bill-json`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        bill_id: parseInt(billId),
        bill_json: billJson,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Update bill status
 */
export const updateBillStatus = async (
  documentId: string,
  status: number
): Promise<BillOperationResponseDTO> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    const response = await fetch(`${API_BASE_URL}/api/bills/update-status`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        id: parseInt(documentId),
        status: status,
      }),
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to update bill status: ${response.statusText}`,
        response.status
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating bill status:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Process bill JSON for GL classification
 */
export const processBillJson = async (
  billData: BillDataDTO
): Promise<BillOperationResponseDTO> => {
  try {
    const response = await fetch(
      `${CORE_API_URL}/gl_classification_interface.php`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          action: "process_bill_json",
          bill_json: JSON.stringify(billData),
        }),
      }
    );

    if (response.ok) {
      return {
        success: true,
        message: "Bill JSON processed successfully",
      };
    }

    throw new APIError("Failed to process bill JSON", response.status);
  } catch (error) {
    console.error("Error processing bill JSON:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Post a bill to the company's accounting backend (Zoho or Tally).
 * Single backend-agnostic endpoint; the backend service decides the backend.
 */
export const createZohoBill = async (
  documentId: string,
  billData: BillDataDTO,
  pdfUrl: string,
  approvalAudit?: ApprovalAuditPayload,
): Promise<BillOperationResponseDTO> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    const companyData = getStoredCompanyData();

    const response = await fetch(`${API_BASE_URL}/api/bills/post-to-accounting`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(
        withApprovalAudit(
          {
            bill_id: parseInt(documentId),
            company_gst_enabled: companyData.companyGstEnabled,
            billJson: billData,
            pdfUrl: pdfUrl,
          },
          approvalAudit,
        ),
      ),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new APIError(
        errorData.message ||
          `Failed to create Zoho bill: ${response.statusText}`,
        response.status
      );
    }

    return {
      success: true,
      message: "Bill created in Zoho successfully",
    };
  } catch (error) {
    console.error("Error creating Zoho bill:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Update bill in Zoho Books
 */
export const updateZohoBill = async (
  documentId: string,
  billData: BillDataDTO,
  approvalAudit?: ApprovalAuditPayload,
): Promise<BillOperationResponseDTO> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    const companyData = getStoredCompanyData();

    const response = await fetch(`${API_BASE_URL}/api/bills/update-in-accounting`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(
        withApprovalAudit(
          {
            bill_id: parseInt(documentId),
            company_gst_enabled: companyData.companyGstEnabled,
            billJson: billData,
          },
          approvalAudit,
        ),
      ),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new APIError(
        errorData.message ||
          `Failed to update Zoho bill: ${response.statusText}`,
        response.status
      );
    }

    return {
      success: true,
      message: "Bill updated in Zoho successfully",
    };
  } catch (error) {
    console.error("Error updating Zoho bill:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Mark bill as duplicate
 */
export const markBillAsDuplicate = async (
  billId: number
): Promise<BillOperationResponseDTO> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    const response = await fetch(
      `${API_BASE_URL}/api/bills/mark-as-duplicate`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: billId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message ||
          `Failed to mark bill as duplicate: ${response.statusText}`,
        response.status
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error marking bill as duplicate:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Reject bill
 */
export const rejectBill = async (
  billId: number
): Promise<BillOperationResponseDTO> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    const response = await fetch(`${API_BASE_URL}/api/bills/reject-bill`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        id: billId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `Failed to reject bill: ${response.statusText}`,
        response.status
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error rejecting bill:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Delete posted bill (from Zoho and soft-delete locally)
 */
export const deletePostedBill = async (
  billId: number
): Promise<BillOperationResponseDTO> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }
  const response = await fetch(`${API_BASE_URL}/api/bills/${billId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to delete bill: ${response.statusText}`,
      response.status
    );
  }
  return data;
};

// Fetch bill counts for dashboard cards
export const fetchBillCounts = async (params?: {
  date_from?: string;
  date_to?: string;
}): Promise<{
  pending_count: number;
  on_hold_count: number;
  posted_count: number;
  duplicate_count: number;
  rejected_count: number;
  rejected_duplicate_count: number;
  under_review_count: number;
}> => {
  try {
    const token = getStoredToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    const queryParams = new URLSearchParams();
    if (params?.date_from) {
      queryParams.append("date_from", params.date_from);
    }
    if (params?.date_to) {
      queryParams.append("date_to", params.date_to);
    }

    const url = `${API_BASE_URL}/api/bills/counts${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error fetching bill counts:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

/**
 * Create manual bill (multipart: bill_json, company_gst_enabled, optional attachments[]).
 * POST /api/bills/manual
 */
export const createManualBill = async (
  billJson: BillDataDTO | Record<string, unknown>,
  companyGstEnabled: number,
  attachments?: File[]
): Promise<{
  success: boolean;
  data?: { id: number; zoho_bill_id?: string; bill_number?: string };
  message?: string;
}> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  const formData = new FormData();
  formData.append("bill_json", JSON.stringify(billJson));
  formData.append("company_gst_enabled", String(companyGstEnabled));
  if (attachments?.length) {
    attachments.forEach((file) => formData.append("attachments[]", file));
  }

  const response = await fetch(`${API_BASE_URL}/api/bills/manual`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to create manual bill: ${response.statusText}`,
      response.status,
      data
    );
  }
  return data;
};

/**
 * Create manual bill draft only (insert + S3, no push to Zoho). For review flow.
 * POST /api/bills/manual with draft: true (JSON) or draft=1 (multipart).
 */
export const createManualBillDraft = async (
  billJson: BillDataDTO | Record<string, unknown>,
  companyGstEnabled: number,
  attachments?: File[]
): Promise<{ success: boolean; data?: { id: number }; message?: string }> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }
  if (attachments?.length) {
    const formData = new FormData();
    formData.append("bill_json", JSON.stringify(billJson));
    formData.append("company_gst_enabled", String(companyGstEnabled));
    formData.append("draft", "1");
    attachments.forEach((file) => formData.append("attachments[]", file));
    const response = await fetch(`${API_BASE_URL}/api/bills/manual`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new APIError(
        data.message || `Failed to create bill draft: ${response.statusText}`,
        response.status,
        data
      );
    }
    return data;
  }
  const response = await fetch(`${API_BASE_URL}/api/bills/manual`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      bill_json: billJson,
      company_gst_enabled: companyGstEnabled,
      draft: true,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to create bill draft: ${response.statusText}`,
      response.status,
      data
    );
  }
  return data;
};

/**
 * Push an existing manual bill draft to Zoho (from review page or approval).
 * POST /api/bills/manual with JSON body: { bill_id, company_gst_enabled [, bill_json ] }.
 * When bill_json is provided (edited form/approval data), backend uses it for the push and persists to the draft row.
 */
export const pushManualBillDraft = async (
  billId: number,
  companyGstEnabled: number,
  billJson?: BillDataDTO | Record<string, unknown> | null,
  approvalAudit?: ApprovalAuditPayload,
): Promise<{
  success: boolean;
  data?: { id: number; zoho_bill_id?: string; bill_number?: string };
  message?: string;
}> => {
  const token = getStoredToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }
  const body: Record<string, unknown> = {
    bill_id: billId,
    company_gst_enabled: companyGstEnabled,
  };
  if (billJson != null && typeof billJson === "object") {
    body.bill_json = billJson;
  }
  const requestBody = withApprovalAudit(body, approvalAudit);
  const response = await fetch(`${API_BASE_URL}/api/bills/manual`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to push manual bill: ${response.statusText}`,
      response.status,
      data
    );
  }
  return data;
};
